<template>
    <div class="containerThumbnailSelector" @click.self="$emit('clickOutside')">
        <div class="thumbnailSelector"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop">
            <div v-if="dragActive" class="drop-overlay">
                <i class="fa-solid fa-cloud-arrow-up"></i>
                <p>Déposez votre image</p>
            </div>
            <div class="ts-tabs">
                <div class="ts-tab" :class="{active: tab === 'upload'}" @click="tab = 'upload'">
                    <i class="fa-solid fa-upload"></i>
                    Importer une image
                </div>
                <div class="ts-tab" :class="{active: tab === 'frame'}" @click="tab = 'frame'">
                    <i class="fa-solid fa-film"></i>
                    Choisir dans la vidéo
                </div>
            </div>

            <div class="ts-tab-content" v-show="tab === 'upload'">
                <p class="ts-hint">Choisissez une image depuis votre ordinateur ou glissez-déposez un fichier.</p>
                <input ref="file" type="file" accept="image/jpeg,image/png,image/webp" hidden @change="onFilePicked">
                <div class="button btn-primary" @click="$refs.file.click()">Choisir un fichier</div>
            </div>

            <div class="ts-tab-content frame" v-show="tab === 'frame'">
                <div class="preview">
                    <video
                        ref="video"
                        :src="effectiveVideoSrc"
                        crossorigin="anonymous"
                        preload="auto"
                        muted
                        playsinline
                        @loadedmetadata="onLoaded"
                    ></video>
                    <div v-if="generating" class="loader">
                        <i class="fa-solid fa-spinner fa-spin"></i>
                        Chargement de la timeline…
                    </div>
                </div>

                <div class="ts-timeline">
                    <div class="strip">
                        <img v-for="(thumb, i) in thumbs" :key="i" :src="thumb" draggable="false">
                    </div>
                    <div class="cursor" :style="{ left: cursorPercent + '%' }"></div>
                    <input
                        type="range"
                        min="0"
                        :max="durationMs || 1"
                        :value="currentMs"
                        step="1"
                        :disabled="generating || !durationMs"
                        @input="onScrub"
                    >
                </div>

                <div class="ts-time">{{ formatTime(currentMs) }} / {{ formatTime(durationMs) }}</div>

                <div class="button btn-primary" :class="{disabled: capturing || generating}" @click="captureFrame">Utiliser cette image</div>
            </div>
        </div>
    </div>
</template>

<script>

const THUMBS_COUNT = 8

export default {
    props: {
        videoSrc: {
            type: String,
            required: true
        },
        videoType: {
            type: String,
            default: ''
        },
        localFile: {
            type: File,
            default: null
        },
        initialTimestampMs: {
            type: Number,
            default: 0
        }
    },

    data() {
        return {
            tab: 'upload',
            capturing: false,
            generating: false,
            thumbs: [],
            durationMs: 0,
            currentMs: 0,
            dragActive: false,
            localVideoUrl: null
        }
    },

    computed: {
        cursorPercent() {
            if (!this.durationMs) return 0
            return (this.currentMs / this.durationMs) * 100
        },
        effectiveVideoSrc() {
            return this.localVideoUrl || this.videoSrc
        }
    },

    mounted() {
        if (this.localFile) {
            this.localVideoUrl = URL.createObjectURL(this.localFile)
        }
    },

    beforeDestroy() {
        if (this.localVideoUrl) URL.revokeObjectURL(this.localVideoUrl)
    },

    methods: {
        async onLoaded() {
            const video = this.$refs.video
            if (!video || !isFinite(video.duration)) return

            this.durationMs = Math.round(video.duration * 1000)
            this.currentMs = Math.min(Math.max(this.initialTimestampMs, 0), this.durationMs)

            await this.generateThumbs()

            if (this.$refs.video) {
                this.$refs.video.currentTime = this.currentMs / 1000
            }
        },

        async generateThumbs() {
            const video = this.$refs.video
            if (!video || !video.videoWidth || !video.videoHeight) return

            this.generating = true

            const width = 160
            const height = Math.round(width * video.videoHeight / video.videoWidth)
            const canvas = document.createElement('canvas')
            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')

            const thumbs = []
            for (let i = 0; i < THUMBS_COUNT; i++) {
                const t = (video.duration * i) / Math.max(THUMBS_COUNT - 1, 1)
                video.currentTime = t
                await new Promise((resolve) => {
                    const onSeeked = () => {
                        video.removeEventListener('seeked', onSeeked)
                        resolve()
                    }
                    video.addEventListener('seeked', onSeeked)
                })
                try {
                    ctx.drawImage(video, 0, 0, width, height)
                    thumbs.push(canvas.toDataURL('image/jpeg', 0.6))
                } catch (e) {
                    thumbs.length = 0
                    break
                }
            }

            this.thumbs = thumbs
            this.generating = false
        },

        onScrub(e) {
            const ms = Number(e.target.value)
            this.currentMs = ms
            if (this.$refs.video) this.$refs.video.currentTime = ms / 1000
        },

        onFilePicked(e) {
            const file = e.target.files[0]
            e.target.value = null
            if (!file) return
            this.$emit('confirmUpload', file)
        },

        onDragOver(e) {
            if (e.dataTransfer && e.dataTransfer.types && Array.prototype.indexOf.call(e.dataTransfer.types, 'Files') >= 0) {
                this.dragActive = true
            }
        },

        onDragLeave(e) {
            if (e.target === e.currentTarget) this.dragActive = false
        },

        onDrop(e) {
            this.dragActive = false
            const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
            if (!file) return
            const allowed = ['image/jpeg', 'image/png', 'image/webp']
            if (!allowed.includes(file.type)) return
            this.$emit('confirmUpload', file)
        },

        formatTime(ms) {
            if (!ms || !isFinite(ms)) return '0:00'
            const totalSec = Math.floor(ms / 1000)
            const m = Math.floor(totalSec / 60)
            const s = totalSec % 60
            return `${m}:${s < 10 ? '0' : ''}${s}`
        },

        captureFrame() {
            const video = this.$refs.video
            if (!video || !video.videoWidth || !video.videoHeight) return
            this.capturing = true

            const canvas = document.createElement('canvas')
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight
            canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)

            canvas.toBlob((blob) => {
                this.capturing = false
                if (!blob) return
                const file = new File([blob], `frame-${Date.now()}.jpg`, { type: 'image/jpeg' })
                this.$emit('confirmFrame', {
                    file,
                    timestampMs: Math.round(video.currentTime * 1000)
                })
            }, 'image/jpeg', 0.92)
        }
    }
}
</script>
